import { useState } from "react";
import { notifyHost } from "@/hooks/useEmbedMessaging";

/**
 * Inline embed for Jason's handwritten teaching notes (PNG scans of his
 * iPad notes). Sits naturally beside prose — frame, light cream background,
 * caption — and opens to a full-screen lightbox on click for legibility.
 *
 * Pass the path under /public, e.g. "/notes/music-theory/finals/01a-twins-and-triplets.png".
 */

interface NoteImageProps {
  src: string;
  caption?: string;
  /** "wide" gives a bigger inline frame, "compact" stays small. Default "wide". */
  size?: "wide" | "compact";
}

export function NoteImage({ src, caption, size = "wide" }: NoteImageProps) {
  const [open, setOpen] = useState(false);
  // A scan that isn't uploaded yet must not show students a broken-image box
  // with a caption describing a picture they can't see. Hide it until it exists.
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  const openLightbox = () => { setOpen(true); notifyHost("companion-modal-open"); };
  const closeLightbox = () => { setOpen(false); notifyHost("companion-modal-close"); };
  const heightClass =
    size === "compact" ? "max-h-[260px]" : "max-h-[420px]";
  return (
    <>
      <figure
        className={`my-5 mx-auto w-full overflow-hidden rounded-2xl border border-amber-500/20 bg-[#fdfaf3] shadow-[0_4px_30px_rgba(0,0,0,0.35)] cursor-zoom-in`}
        onClick={openLightbox}
        role="button"
        aria-label="Click to enlarge handwritten note"
      >
        <img
          src={src}
          alt={caption ?? "Handwritten note from Jason Zac"}
          className={`block ${heightClass} w-auto mx-auto select-none`}
          loading="lazy"
          onError={() => setFailed(true)}
        />
        {caption && (
          <figcaption className="border-t border-amber-500/15 bg-[#f9f3e1]/80 px-4 py-2 text-[12px] italic text-zinc-700">
            {caption}
          </figcaption>
        )}
      </figure>
      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm cursor-zoom-out p-4"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
        >
          <img
            src={src}
            alt={caption ?? ""}
            className="max-h-[95vh] max-w-[95vw] rounded-xl bg-[#fdfaf3] p-2 shadow-2xl"
          />
        </div>
      )}
    </>
  );
}

/** Two-up grid for placing two related notes side by side. Falls back to stacked on mobile. */
export function NoteImagePair({
  notes,
}: {
  notes: { src: string; caption?: string }[];
}) {
  return (
    <div className="my-5 grid gap-4 md:grid-cols-2">
      {notes.map((n) => (
        <NoteImage key={n.src} src={n.src} caption={n.caption} size="compact" />
      ))}
    </div>
  );
}
